import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUserGraduate, FaBook, FaClock, FaPlay, FaSpinner, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import { notifySuccess, notifyError } from '../../utils/notifications';
import { useAuth } from '../../context/AuthContext';
import { fetchMyEnrollments, unenrollFromCourse } from '../../services/courseService';
import { formatDuration } from '../../lib/time';
import usePagination from '../../hooks/usePagination';
import SEO from '../../components/common/SEO';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Pagination from '../../components/common/Pagination';
import AdminEmptyState from '../Admin/components/AdminEmptyState';
import { AdminStat } from '../Admin/components/AdminListCard';
import DashboardShell from './DashboardShell';

const PAGE_SIZE = 6;

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user, isStudent, isAuthenticated, getAuthHeaders } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unenrollingId, setUnenrollingId] = useState(null);
  const [unenrollDialog, setUnenrollDialog] = useState({ isOpen: false, courseId: null, courseTitle: '' });
  const pager = usePagination(enrollments, PAGE_SIZE);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/dashboard/student' } });
      return;
    }
    if (!isStudent) {
      notifyError('Access denied. Students only.');
      navigate('/');
    }
  }, [isAuthenticated, isStudent, navigate]);

  const loadEnrollments = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchMyEnrollments(getAuthHeaders());
      setEnrollments(data);
    } catch (err) {
      notifyError(err.message || 'Failed to load enrollments');
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (isStudent) loadEnrollments();
  }, [isStudent, loadEnrollments]);

  const confirmUnenroll = (courseId, courseTitle) => {
    setUnenrollDialog({ isOpen: true, courseId, courseTitle });
  };

  const handleUnenrollConfirmed = async () => {
    const { courseId, courseTitle } = unenrollDialog;
    setUnenrollDialog({ isOpen: false, courseId: null, courseTitle: '' });
    setUnenrollingId(courseId);
    try {
      await unenrollFromCourse(courseId, getAuthHeaders());
      setEnrollments((prev) => prev.filter((e) => e.course_id !== courseId));
      notifySuccess(`Unenrolled from "${courseTitle}".`);
    } catch (err) {
      notifyError(err.message || 'Failed to unenroll');
    } finally {
      setUnenrollingId(null);
    }
  };

  if (!isStudent) return null;

  const classesWatched = enrollments.reduce((n, e) => n + (Number(e.completed_lessons) || 0), 0);
  const totalClasses = enrollments.reduce((n, e) => n + (Number(e.available_lessons) || 0), 0);
  const totalWatched = enrollments.reduce((n, e) => n + (Number(e.watched_seconds) || 0), 0);

  return (
    <DashboardShell
      role="student"
      title={`Welcome${user?.name ? `, ${user.name}` : ''}`}
      subtitle="Track your learning progress"
      headerActions={(
        <>
          <Link
            to="/courses"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-designer-primary hover:bg-red-600 text-white text-sm font-semibold transition-colors shadow-sm"
          >
            <FaBook className="w-3.5 h-3.5" />
            Browse Courses
          </Link>
          <Link
            to="/account/password"
            className="inline-flex items-center px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:text-designer-primary hover:border-designer-primary/40 transition-colors"
          >
            Change password
          </Link>
        </>
      )}
    >
      <SEO title="Student Dashboard | Arvix Academy" description="Your enrolled courses on Arvix Academy" />
      <ConfirmDialog
        isOpen={unenrollDialog.isOpen}
        onClose={() => setUnenrollDialog({ isOpen: false, courseId: null, courseTitle: '' })}
        onConfirm={handleUnenrollConfirmed}
        title="Unenroll from Course"
        message={`Are you sure you want to unenroll from "${unenrollDialog.courseTitle}"? You can always re-enroll later.`}
        confirmText="Yes, Unenroll"
        cancelText="Cancel"
        type="warning"
      />

      <div className="space-y-6 animate-fadeIn">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <AdminStat icon={FaUserGraduate} label="Enrolled Courses" value={enrollments.length} tile="bg-red-50 text-designer-primary" />
          <AdminStat icon={FaCheckCircle} label="Classes Completed" value={classesWatched} sub={`of ${totalClasses} available`} tile="bg-green-50 text-green-600" />
          <AdminStat icon={FaClock} label="Watch Time" value={formatDuration(totalWatched) || '0m'} sub="across all courses" tile="bg-blue-50 text-blue-600" />
        </div>

        {/* Enrolled courses */}
        <div>
          <h2 className="text-lg font-bold text-designer-dark mb-4">My Enrolled Courses ({enrollments.length})</h2>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <FaSpinner className="animate-spin text-designer-primary w-8 h-8" />
            </div>
          ) : enrollments.length === 0 ? (
            <AdminEmptyState
              icon={FaBook}
              title="No enrollments yet"
              description="Browse our courses and start learning today"
            />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" ref={pager.listRef}>
                {pager.items.map((enrollment) => (
                  <article
                    key={enrollment.enrollment_id}
                    className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-5 hover:shadow-md transition-shadow flex flex-col"
                  >
                    <h3 className="font-bold text-designer-dark text-base">{enrollment.title}</h3>
                    <p className="text-gray-500 text-sm line-clamp-2 mt-1 mb-3">{enrollment.description}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-4">
                      {enrollment.duration && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <FaClock className="text-designer-primary w-3 h-3" />
                          {enrollment.duration}
                        </span>
                      )}
                      {enrollment.level && (
                        <span className="text-[11px] px-2 py-0.5 bg-red-50 text-designer-primary rounded-md font-semibold">
                          {enrollment.level}
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        by {enrollment.teacher_name}
                      </span>
                    </div>
                    {enrollment.slug && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                          <span>
                            {enrollment.available_lessons
                              ? `${enrollment.completed_lessons} of ${enrollment.available_lessons} classes completed`
                              : 'Recorded classes are being uploaded'}
                          </span>
                          {enrollment.available_lessons > 0 && (
                            <span className="font-semibold text-designer-primary">
                              {Math.round((enrollment.completed_lessons / enrollment.available_lessons) * 100)}%
                            </span>
                          )}
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-designer-primary to-red-600 rounded-full"
                            style={{ width: `${enrollment.available_lessons ? (enrollment.completed_lessons / enrollment.available_lessons) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="mt-2.5 flex items-center justify-between gap-3">
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                            <FaClock className="text-designer-primary w-3 h-3" />
                            {formatDuration(Number(enrollment.watched_seconds) || 0) || '0m'} watched
                          </span>
                          <Link
                            to={`/learn/${enrollment.slug}`}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-designer-primary hover:bg-red-600 text-white text-sm font-semibold rounded-xl transition-colors"
                          >
                            <FaPlay className="w-3 h-3" />
                            {enrollment.completed_lessons ? 'Continue watching' : 'Start watching'}
                          </Link>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-400">
                        Enrolled {new Date(enrollment.enrolled_at).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'short', day: 'numeric'
                        })}
                      </p>
                      {enrollment.enrollment_mode !== 'managed' && (
                        <button
                          type="button"
                          onClick={() => confirmUnenroll(enrollment.course_id, enrollment.title)}
                          disabled={unenrollingId === enrollment.course_id}
                          className="text-xs text-gray-400 hover:text-red-600 font-semibold transition-colors disabled:opacity-50"
                        >
                          {unenrollingId === enrollment.course_id ? 'Unenrolling...' : 'Unenroll'}
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
              <Pagination currentPage={pager.page} totalPages={pager.totalPages} onPageChange={pager.changePage} />
            </>
          )}
          {enrollments.length === 0 && !isLoading && (
            <div className="text-center mt-4">
              <Link
                to="/courses"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-designer-primary hover:bg-red-600 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Explore Courses
                <FaArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
};

export default StudentDashboard;
